import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../js/api/messaging";
import { useAuth } from "../../js/hooks/useAuth";
import { useBrokerDesk } from "../../js/hooks/useBrokerDesk";
import { useJourneyLive } from "../../js/hooks/useJourneyLive";
import { BROKER_DESK } from "../../js/api/brokerDesk";
import { formatDate, prettyValue } from "../../js/utils/formatters";
import { toTitleCase } from "../../js/utils/titleCase";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "message", label: "Notes", match: ["message.create", "thread.create", "thread.archive"] },
  { id: "export", label: "Reports", match: ["export.generate"] },
  { id: "archive", label: "Archive", match: ["transaction.archive", "email.queue"] },
  { id: "status", label: "Status", match: ["transaction.status_change", "email.opt_in"] },
];

function formatStamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)} · ${date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

export default function AuditLogView({ title = "Retention log" }) {
  const { session } = useAuth();
  const { active, mode } = useBrokerDesk();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [fileId, setFileId] = useState("");
  const [sort, setSort] = useState({ key: "when", dir: "desc" });

  const load = useCallback(async () => {
    if (!session?.userId) return;
    const result = await api("GET", "/api/audit", null, session);
    if (!result.ok) {
      setError(result.error || "Could not load the retention log.");
      setRows([]);
      return;
    }
    setError("");
    setRows(result.data || []);
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  useJourneyLive(load);

  const scoped = useMemo(() => {
    if (!active) return rows;
    return rows.filter((row) => {
      if (!row.agent_id) return true;
      const mineRow = row.agent_id === session?.userId;
      return mode === BROKER_DESK.mine ? mineRow : !mineRow;
    });
  }, [rows, active, mode, session?.userId]);

  const files = useMemo(() => {
    const map = new Map();
    scoped.forEach((row) => {
      if (row.transaction_id && row.property_address) {
        map.set(row.transaction_id, row.property_address);
      }
    });
    return [...map.entries()];
  }, [scoped]);

  const visible = useMemo(() => {
    const chip = FILTERS.find((item) => item.id === filter);
    const filtered = scoped.filter((row) => {
      if (chip?.match && !chip.match.includes(row.action_type)) return false;
      if (fileId && row.transaction_id !== fileId) return false;
      return true;
    });
    const copy = [...filtered];
    copy.sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "who") return String(a.actor_name || "").localeCompare(String(b.actor_name || "")) * dir;
      if (sort.key === "action") return String(a.label || "").localeCompare(String(b.label || "")) * dir;
      if (sort.key === "file") return String(a.property_address || "").localeCompare(String(b.property_address || "")) * dir;
      if (sort.key === "detail") return String(a.detail || "").localeCompare(String(b.detail || "")) * dir;
      return String(a.timestamp || "").localeCompare(String(b.timestamp || "")) * dir;
    });
    return copy;
  }, [scoped, filter, fileId, sort]);

  function toggleSort(key) {
    setSort((current) =>
      current.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  function sortMark(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ↑" : " ↓";
  }

  return (
    <div className="audit-log" data-tour="retention">
      <header className="audit-log__intro">
        <h1>{toTitleCase(title)}</h1>
        <p className="page-lead">
          A quiet record of notes, reports, archive, and status changes. This is a retention log for
          supervision — not a California DRE certification.
        </p>
        <div className="audit-log__filters">
          <nav className="messages-desk__tabs" aria-label="Filter the log">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`messages-tab${filter === item.id ? " is-active" : ""}`}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          {files.length > 1 ? (
            <label className="audit-log__file">
              <span className="muted">File</span>
              <select
                className="field-select"
                value={fileId}
                onChange={(event) => setFileId(event.target.value)}
              >
                <option value="">All files</option>
                {files.map(([id, address]) => (
                  <option key={id} value={id}>
                    {address}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </header>
      {error ? <p className="field-error">{error}</p> : null}
      {visible.length === 0 ? (
        <p className="muted">No matching events yet. Activity on files you can see will appear here.</p>
      ) : (
        <div className="audit-table-wrap">
          <table className="audit-table sort-table">
            <thead>
              <tr>
                <th>
                  <button type="button" onClick={() => toggleSort("when")}>
                    When{sortMark("when")}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("who")}>
                    Who{sortMark("who")}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("action")}>
                    Action{sortMark("action")}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("file")}>
                    File{sortMark("file")}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("detail")}>
                    Detail{sortMark("detail")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id}>
                  <td>{formatStamp(row.timestamp)}</td>
                  <td>
                    {row.actor_name || "System"}
                    {row.actor_role ? <span className="muted"> · {prettyValue(row.actor_role)}</span> : null}
                  </td>
                  <td>{row.label}</td>
                  <td>
                    {row.property_address || "—"}
                    {row.client_role ? <span className="muted"> · {prettyValue(row.client_role)}</span> : null}
                  </td>
                  <td>{row.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
